// App.tsx
//
// Minimal React chat UI for the life story interviewer MVP
//
// Features:
// - Start session
// - Chat interface
// - Send messages
// - Mobile friendly
// - Calls Django backend
//
// Assumes backend endpoints:
// POST /api/start/
// POST /api/turn/

import { useState, useEffect, useRef, useCallback } from "react";
import BookPlanReview from "./BookPlanReview";
import StoryReviewView from "./StoryReview";

type Message = {
  role: "user" | "assistant";
  content: string;
};

type AppStage =
  | "home"
  | "interview"
  | "story_review"
  | "book_plan_review"
  | "book_generation_progress";

type VoiceTurnStatus =
  | "recording"
  | "acknowledging"
  | "transcribing"
  | "reflecting"
  | "speaking";

type RecordingFlowState =
  | "idle"
  | "starting"
  | "recording"
  | "stopping"
  | "uploading"
  | "reflecting"
  | "speaking"
  | "error";

type BookPlanChapter = {
  chapter_number: number;
  title: string;
  purpose: string;
  missing_details?: string[];
};

export type BookPlan = {
  title: string;
  book_arc: string;
  chapters?: BookPlanChapter[];
};

export type BookGenerationStatus = {
  job_id: number;
  status: "queued" | "running" | "completed" | "failed";
  stage?: string;
  progress_percent?: number;
  status_message?: string;
  download_url?: string;
  error_message?: string;
};

export type StoryGapStatus =
  | "open"
  | "in_progress"
  | "answered"
  | "skipped";

export type StoryGapQuestion = {
  id: number;
  title: string;
  question: string;
  rationale?: string;
  source: string;
  priority: number;
  status: StoryGapStatus;
};

export type StoryCoverage = Record<string, "missing" | "medium" | "strong">;

export type StoryReadiness = {
  status: "needs_more_detail" | "almost_ready" | "ready";
  message: string;
  total_gaps: number;
  open_gaps: number;
  in_progress_gaps: number;
  answered_gaps: number;
  skipped_gaps: number;
  resolved_gaps: number;
  completeness_percent: number;
  book_plan_available: boolean;
};

export type StoryReview = {
  journey_id: number;
  canonical_journey_id?: number;
  story_so_far: string[];
  coverage: StoryCoverage;
  readiness: StoryReadiness;
  open_questions: StoryGapQuestion[];
  all_gap_counts: {
    open: number;
    in_progress: number;
    answered: number;
    skipped: number;
    archived?: number;
  };
};

const BACKEND_BASE_URLS = {
  local: "http://localhost:8000",
  production: "https://api.lifestorysimply.com",
} as const;

type BackendEnvironment = keyof typeof BACKEND_BASE_URLS;

const backendEnvironment = (
  process.env.REACT_APP_BACKEND_ENV ||
  (process.env.NODE_ENV === "production" ? "production" : "local")
) as BackendEnvironment;

const BACKEND_BASE_URL =
  BACKEND_BASE_URLS[backendEnvironment] ||
  BACKEND_BASE_URLS.local;

const API_BASE = `${BACKEND_BASE_URL}/api`;
const SESSION_STORAGE_KEY = "life_story_session_id";
const JOURNEY_STORAGE_KEY = "life_story_journey_id";
const BOOK_GENERATION_JOB_STORAGE_KEY =
  "life_story_book_generation_job_id";

const PHASE_LABELS: Record<string, string> = {
  life_map: "Exploring your life story",
  story_expansion: "Adding detail to important memories",
  meaning_reflection: "Reflecting on meaning and legacy",
  legacy_completion: "Preparing your story keepsake",
};

const BOOK_GENERATION_STATUS_MESSAGES: Record<
  BookGenerationStatus["status"],
  string
> = {
  queued: "Your book is queued for generation.",
  running: "Your book is being generated. This may take a few minutes.",
  completed: "Your book is ready.",
  failed: "Something went wrong while creating your book.",
};

async function parseJsonResponse(response: Response, fallbackMessage: string) {
  if (!response.ok) {
    throw new Error(fallbackMessage);
  }

  return response.json();
}

function getRecordingMimeType() {
  if (
    typeof MediaRecorder !== "undefined" &&
    typeof MediaRecorder.isTypeSupported === "function" &&
    MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
  ) {
    return "audio/webm;codecs=opus";
  }

  if (
    typeof MediaRecorder !== "undefined" &&
    typeof MediaRecorder.isTypeSupported === "function" &&
    MediaRecorder.isTypeSupported("audio/webm")
  ) {
    return "audio/webm";
  }

  return "";
}

const TTS_VOICE = "nova";
const SHOW_DEBUG_TIMINGS =
  localStorage.getItem("SHOW_DEBUG_TIMINGS") === "true";
const DEBUG_AUDIO = localStorage.getItem("DEBUG_AUDIO") === "true";
const USE_CHUNKED_AUDIO = true;
const CHUNK_INTERVAL_MS = 30000;
const MIN_RECORDING_MS = 1500;

function audioDebug(message: string, data?: unknown) {
  if (!DEBUG_AUDIO) return;

  console.log(
    `[AudioFlow] ${new Date().toISOString()} - ${message}`,
    data || ""
  );
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function expectedChunkCountFromDuration(
  durationMs: number | null,
  chunkIntervalMs = CHUNK_INTERVAL_MS
) {
  if (!durationMs || durationMs <= 0) return null;

  return Math.max(1, Math.ceil(durationMs / chunkIntervalMs));
}

function HomeScreen({
  message,
  canReviewStoryProgress,
  loading,
  loadingStoryReview,
  onStartNewSession,
  onReviewStoryProgress,
}: {
  message?: string | null;
  canReviewStoryProgress: boolean;
  loading: boolean;
  loadingStoryReview: boolean;
  onStartNewSession: () => void;
  onReviewStoryProgress: () => void;
}) {
  return (
    <div style={styles.page}>
      <div style={{ ...styles.container, ...styles.homeContainer }}>
        <div style={styles.homeContent}>
          <h1 style={styles.homeTitle}>Life Story Simply</h1>
          {message ? (
            <p style={styles.homeSavedMessage}>{message}</p>
          ) : (
            <p style={styles.homeIntro}>
              Continue preserving your story, one reflection at a time.
            </p>
          )}
          <div style={styles.homeActions}>
            <button
              type="button"
              style={{ ...styles.startButton, ...styles.homePrimaryButton }}
              onClick={onStartNewSession}
              disabled={loading}
            >
              {loading ? "Starting..." : "Start new session"}
            </button>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={onReviewStoryProgress}
              disabled={!canReviewStoryProgress || loadingStoryReview}
            >
              {loadingStoryReview
                ? "Loading Story Progress..."
                : "Review Story Progress"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BookGenerationProgress({
  status,
  error,
  onBackToStoryReview,
}: {
  status: BookGenerationStatus | null;
  error: string | null;
  onBackToStoryReview: () => void;
}) {
  const progress = Math.max(
    0,
    Math.min(100, status?.progress_percent ?? 0)
  );
  const message =
    status?.status_message ||
    (status?.status === "completed"
      ? "Your book is ready."
      : "Creating your book...");
  const pollingText =
    status?.status === "completed"
      ? "Status polling complete."
      : status?.status === "failed"
        ? "Status polling stopped."
        : error
          ? "Retrying status check every few seconds."
          : "Checking status every few seconds.";

  return (
    <div style={styles.bookProgressContainer}>
      <div style={styles.bookProgressHeader}>
        <h2 style={styles.bookProgressTitle}>Creating your book</h2>
        {status?.stage && (
          <span style={styles.bookProgressStage}>
            {status.stage.replace(/_/g, " ")}
          </span>
        )}
      </div>
      <p style={styles.bookProgressMessage}>{message}</p>

      <div
        role="progressbar"
        aria-label="Book generation progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        style={styles.bookProgressOuter}
      >
        <div
          style={{
            ...styles.bookProgressInner,
            width: `${progress}%`,
          }}
        />
      </div>

      <div style={styles.bookProgressMeta}>
        <strong>{progress}%</strong>
        <span>{pollingText}</span>
      </div>

      {error && <div style={styles.errorMessage}>{error}</div>}

      {status?.status === "completed" && status.download_url && (
        <a
          href={status.download_url}
          target="_blank"
          rel="noreferrer"
          style={styles.primaryLinkButton}
        >
          Download Book
        </a>
      )}

      {status?.status !== "completed" && (
        <p style={styles.smallMutedText}>
          You can leave this page open while your book is being prepared.
        </p>
      )}

      <button
        type="button"
        style={styles.secondaryButton}
        onClick={onBackToStoryReview}
      >
        Back to Story Progress
      </button>
    </div>
  );
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs = 30000
) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

function resolveBackendUrl(url: string) {
  return new URL(url, BACKEND_BASE_URL).toString();
}

function normalizeBookGenerationStatus(
  data: any,
  fallbackJobId?: number
): BookGenerationStatus {
  const rawStatus = data?.status;
  const status: BookGenerationStatus["status"] =
    rawStatus === "running" ||
    rawStatus === "completed" ||
    rawStatus === "failed" ||
    rawStatus === "queued"
      ? rawStatus
      : "queued";
  const rawJobId = data?.job_id ?? fallbackJobId;
  const jobId = Number(rawJobId);
  const rawProgress = Number(data?.progress_percent);
  const progressPercent = Number.isFinite(rawProgress)
    ? Math.max(0, Math.min(100, rawProgress))
    : status === "completed"
      ? 100
      : 0;

  return {
    job_id: Number.isFinite(jobId) ? jobId : fallbackJobId || 0,
    status,
    stage: data?.stage || status,
    progress_percent: progressPercent,
    status_message:
      data?.status_message || BOOK_GENERATION_STATUS_MESSAGES[status],
    download_url:
      typeof data?.download_url === "string"
        ? resolveBackendUrl(data.download_url)
        : undefined,
    error_message:
      typeof data?.error_message === "string"
        ? data.error_message
        : undefined,
  };
}

function isPlaybackBlockedError(err: unknown) {
  return (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name?: string }).name === "NotAllowedError"
  );
}

export default function App() {
  const [appStage, setAppStage] = useState<AppStage>(() =>
    localStorage.getItem(SESSION_STORAGE_KEY) ? "interview" : "home"
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [journeyId, setJourneyId] = useState<string | null>(() =>
    localStorage.getItem(JOURNEY_STORAGE_KEY)
  );
  const [currentPhase, setCurrentPhase] = useState<string | null>(null);

  const [messages, setMessages] = useState<Message[]>([]);

  const [input, setInput] = useState("");

  const [loading, setLoading] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<string | null>(
    null
  );

  const [bookPlan, setBookPlan] = useState<BookPlan | null>(null);
  const [showPlanReview, setShowPlanReview] = useState(false);
  const [storyReview, setStoryReview] = useState<StoryReview | null>(
    null
  );
  const [showStoryReview, setShowStoryReview] = useState(false);
  const [loadingStoryReview, setLoadingStoryReview] = useState(false);
  const [storyReviewError, setStoryReviewError] = useState<string | null>(
    null
  );
  const [startingStoryGapId, setStartingStoryGapId] =
    useState<number | null>(null);
  const [skippingStoryGapId, setSkippingStoryGapId] =
    useState<number | null>(null);
  const [bookGenerationJobId, setBookGenerationJobId] =
    useState<number | null>(null);
  const [bookGenerationStatus, setBookGenerationStatus] =
    useState<BookGenerationStatus | null>(null);
  const [bookDownloadUrl, setBookDownloadUrl] =
    useState<string | null>(null);
  const [bookGenerationError, setBookGenerationError] =
    useState<string | null>(null);
  const [showBookGenerationProgress, setShowBookGenerationProgress] =
    useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const currentTurnIdRef = useRef<number | null>(null);
  const chunkIndexRef = useRef(0);
  const chunkUploadPromisesRef = useRef<Promise<unknown>[]>([]);
  const recordedBlobsRef = useRef<Blob[]>([]);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingStoppedAtRef = useRef<number | null>(null);
  const stopRequestedRef = useRef(false);
  const finishStartedRef = useRef(false);
  const onStopHandledRef = useRef(false);
  const stopFinalizeTimeoutRef = useRef<number | null>(null);
  const recordedChunkCountRef = useRef(0);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordingFlowState, setRecordingFlowState] =
    useState<RecordingFlowState>("idle");
  const [recordingError, setRecordingError] = useState<string | null>(
    null
  );
  const [processingVoiceTurn, setProcessingVoiceTurn] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [doneForNowMessage, setDoneForNowMessage] = useState<string | null>(
    null
  );
  const [doneForNowError, setDoneForNowError] = useState<string | null>(
    null
  );
  const [, setCurrentTurnId] = useState<number | null>(null);
  const [uploadedChunkCount, setUploadedChunkCount] = useState(0);
  const [voiceTurnStatus, setVoiceTurnStatus] =
    useState<VoiceTurnStatus | null>(null);
  const [lastTurnTimings, setLastTurnTimings] = useState<any | null>(
    null
  );
  const [processingAckAudioUrl, setProcessingAckAudioUrl] =
    useState<string | null>(null);
  const [processingAckText, setProcessingAckText] =
    useState<string | null>(null);
  const [latestSpeechAudioUrl, setLatestSpeechAudioUrl] =
    useState<string | null>(null);
  const [speechPlaybackBlocked, setSpeechPlaybackBlocked] = useState(false);
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);
  const optionsMenuRef = useRef<HTMLDivElement | null>(null);
  const initialSessionStartedRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const processingAckAudioRef = useRef<HTMLAudioElement | null>(null);
  const processingAckPlayPromiseRef = useRef<Promise<void> | null>(null);
  const selectedVoice = TTS_VOICE;

  const updateJourneyFromResponse = useCallback((data: any) => {
    const canonicalJourneyId =
      data?.canonical_journey_id || data?.journey_id;

    if (canonicalJourneyId) {
      setJourneyId(String(canonicalJourneyId));
      localStorage.setItem(
        JOURNEY_STORAGE_KEY,
        String(canonicalJourneyId)
      );
    }

    if (data?.session_id) {
      setSessionId(String(data.session_id));
      localStorage.setItem(SESSION_STORAGE_KEY, String(data.session_id));
    }

    if (data?.was_merged_redirect) {
      console.log(
        "Journey was merged; updated localStorage to canonical journey",
        {
          from: data.merged_from_journey_id,
          to: canonicalJourneyId,
        }
      );

      if (DEBUG_AUDIO || SHOW_DEBUG_TIMINGS) {
        console.info(
          `Merged journey redirect: ${data.merged_from_journey_id} -> ${canonicalJourneyId}`
        );
      }
    }
  }, []);

  function clearStopFinalizeTimeout() {
    if (stopFinalizeTimeoutRef.current) {
      window.clearTimeout(stopFinalizeTimeoutRef.current);
      stopFinalizeTimeoutRef.current = null;
    }
  }

  const logMediaStreamState = useCallback((label: string) => {
    const stream = mediaStreamRef.current;

    audioDebug(label, {
      hasStream: !!stream,
      tracks: stream
        ? stream.getTracks().map((track) => ({
            kind: track.kind,
            readyState: track.readyState,
            enabled: track.enabled,
            muted: track.muted,
          }))
        : [],
    });
  }, []);

  const stopMediaStream = useCallback(() => {
    audioDebug("stopMediaStream called");

    const stream = mediaStreamRef.current;

    if (stream) {
      stream.getTracks().forEach((track) => {
        try {
          audioDebug("Stopping media track", {
            kind: track.kind,
            readyState: track.readyState,
          });
          track.stop();
        } catch (err) {
          audioDebug("Error stopping media track", err);
        }
      });
    }

    mediaStreamRef.current = null;
    logMediaStreamState("after stopMediaStream");
  }, [logMediaStreamState]);

  function cleanupRecordingResources() {
    audioDebug("cleanupRecordingResources");
    clearStopFinalizeTimeout();

    try {
      const recorder = mediaRecorderRef.current;

      if (recorder && recorder.state !== "inactive") {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
        recorder.stop();
      }
    } catch (err) {
      audioDebug("Error stopping stale recorder", err);
    }

    stopMediaStream();

    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    currentTurnIdRef.current = null;
    chunkIndexRef.current = 0;
    chunkUploadPromisesRef.current = [];
    recordedBlobsRef.current = [];
    recordingStartedAtRef.current = null;
    recordingStoppedAtRef.current = null;
    stopRequestedRef.current = false;
    finishStartedRef.current = false;
    onStopHandledRef.current = false;
    recordedChunkCountRef.current = 0;
    setCurrentTurnId(null);
    setUploadedChunkCount(0);
    setRecording(false);
  }

  function getVoiceTurnStatusText() {
    switch (voiceTurnStatus) {
      case "recording":
        return uploadedChunkCount > 0
          ? `Listening. Take your time. Saved ${uploadedChunkCount} part${
              uploadedChunkCount === 1 ? "" : "s"
            }.`
          : "Listening. Take your time.";
      case "acknowledging":
        return (
          processingAckText ||
          "Thank you. Taking a moment with what you shared."
        );
      case "transcribing":
        return "Listening back to your story...";
      case "reflecting":
        return "Preparing a thoughtful response...";
      case "speaking":
        return "Responding...";
      default:
        return null;
    }
  }

  const resetBookGenerationState = useCallback(
    (clearStoredJob = true) => {
      setBookGenerationJobId(null);
      setBookGenerationStatus(null);
      setBookDownloadUrl(null);
      setBookGenerationError(null);
      setShowBookGenerationProgress(false);

      if (clearStoredJob) {
        localStorage.removeItem(BOOK_GENERATION_JOB_STORAGE_KEY);
      }
    },
    []
  );

  const pollBookGenerationStatus = useCallback(
    async (jobId: number) => {
      try {
        const response = await fetch(
          `${API_BASE}/book-generation/${jobId}/`
        );

        const data = await parseJsonResponse(
          response,
          "Failed to check book generation status."
        );

        updateJourneyFromResponse(data);

        const nextStatus = normalizeBookGenerationStatus(data, jobId);
        setBookGenerationStatus(nextStatus);
        setBookDownloadUrl(nextStatus.download_url || null);

        if (nextStatus.status === "completed") {
          setShowBookGenerationProgress(true);
          setBookGenerationError(null);
          return "completed";
        }

        if (nextStatus.status === "failed") {
          setShowBookGenerationProgress(true);
          setBookGenerationError(
            nextStatus.error_message ||
              "Something went wrong while creating your book."
          );
          return "failed";
        }

        setBookGenerationError(null);
        return "running";
      } catch (err) {
        console.error("Failed polling book generation status", err);
        setBookGenerationError(
          "Could not check book generation status. Please try again."
        );
        return "error";
      }
    },
    [updateJourneyFromResponse]
  );

  const playAudioUrl = useCallback(
    (
      url: string,
      options: {
        onPlaying?: () => void;
        onError?: (err: unknown) => void;
      } = {}
    ) => {
      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(url);
      audio.preload = "auto";
      audio.onplaying = () => {
        if (audioRef.current === audio) {
          options.onPlaying?.();
        }
      };
      audio.onerror = () => {
        if (audioRef.current === audio) {
          options.onError?.(audio.error || new Error("Audio failed to load"));
        }
      };
      audioRef.current = audio;

      audio.load();

      return audio.play();
    },
    []
  );

  const playProcessingAcknowledgementAudio = useCallback(
    (audioUrl: string | null | undefined) => {
      if (!audioUrl) {
        audioDebug("No processing acknowledgement audio URL available");
        return;
      }

      try {
        const existingAudio = processingAckAudioRef.current;

        if (existingAudio) {
          existingAudio.pause();
          existingAudio.src = "";
          processingAckAudioRef.current = null;
        }

        const audio = new Audio(audioUrl);
        processingAckAudioRef.current = audio;
        audio.preload = "auto";
        audio.onplay = () => {
          audioDebug("Processing acknowledgement audio started", {
            audioUrl,
            duration: audio.duration,
          });
        };
        audio.onended = () => {
          audioDebug("Processing acknowledgement audio ended", {
            audioUrl,
          });

          if (processingAckAudioRef.current === audio) {
            processingAckAudioRef.current = null;
          }

          processingAckPlayPromiseRef.current = null;
        };
        audio.onerror = () => {
          audioDebug("Processing acknowledgement audio error", {
            audioUrl,
            error: audio.error,
          });

          if (processingAckAudioRef.current === audio) {
            processingAckAudioRef.current = null;
          }

          processingAckPlayPromiseRef.current = null;
        };

        const playPromise = audio.play();
        processingAckPlayPromiseRef.current = playPromise
          .then(() => {
            audioDebug("Processing acknowledgement audio play promise resolved");
          })
          .catch((err) => {
            audioDebug(
              "Processing acknowledgement audio play promise rejected",
              err
            );

            if (processingAckAudioRef.current === audio) {
              processingAckAudioRef.current = null;
            }

            processingAckPlayPromiseRef.current = null;
          });
      } catch (err) {
        audioDebug("Failed to start processing acknowledgement audio", err);
      }
    },
    []
  );

  const waitForProcessingAcknowledgementToFinish = useCallback(
    async (maxWaitMs = 2500) => {
      const audio = processingAckAudioRef.current;

      if (!audio || audio.ended || audio.paused) {
        return;
      }

      audioDebug("Waiting briefly for processing acknowledgement to finish", {
        currentTime: audio.currentTime,
        duration: audio.duration,
      });

      await Promise.race([
        new Promise<void>((resolve) => {
          const cleanup = () => {
            audio.removeEventListener("ended", cleanup);
            audio.removeEventListener("error", cleanup);
            resolve();
          };

          audio.addEventListener("ended", cleanup);
          audio.addEventListener("error", cleanup);
        }),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, maxWaitMs);
        }),
      ]);
    },
    []
  );

  const preloadProcessingAcknowledgment = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/processing-acknowledgment/?voice=${selectedVoice}`
      );

      const data = await parseJsonResponse(
        response,
        "Failed to preload processing acknowledgment"
      );

      if (typeof data.text === "string") {
        setProcessingAckText(data.text);
      }

      if (typeof data.audio_url === "string") {
        setProcessingAckAudioUrl(resolveBackendUrl(data.audio_url));
      }
    } catch (err) {
      console.error("Failed to preload processing acknowledgment", err);
    }
  }, [selectedVoice]);

  const startOrResumeSession = useCallback(
    async (
      existingSessionId?: string | null,
      existingJourneyId?: string | null,
      options: { forceNewSession?: boolean } = {}
    ) => {
      try {
        setLoading(true);

        const body: any = {};
        if (existingSessionId) {
          body.session_id = existingSessionId;
        }
        if (existingJourneyId) {
          body.journey_id = existingJourneyId;
        }
        if (options.forceNewSession) {
          body.force_new_session = true;
        }

        const response = await fetch(`${API_BASE}/start/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        const data = await parseJsonResponse(
          response,
          "Could not start or resume session"
        );

        updateJourneyFromResponse(data);
        setCurrentPhase(data.current_phase || null);
        setSessionStatus(data.status);
        setAppStage("interview");

        if (data.messages) {
          setMessages(data.messages);
        } else if (data.response) {
          setMessages([
            {
              role: "assistant",
              content: data.response,
            },
          ]);
        }

        if (typeof data.audio_url === "string") {
          const audioUrl = resolveBackendUrl(data.audio_url);

          setLatestSpeechAudioUrl(audioUrl);
          setSpeechPlaybackBlocked(true);
          console.info("Initial prompt audio URL received", audioUrl);

          void playAudioUrl(audioUrl, {
            onPlaying: () => setSpeechPlaybackBlocked(false),
            onError: (err) => {
              console.warn("Initial prompt audio failed to load", err);
              setSpeechPlaybackBlocked(true);
            },
          }).catch((err) => {
            if (!isPlaybackBlockedError(err)) {
              console.warn("Initial prompt audio playback failed", err);
            }

            setSpeechPlaybackBlocked(true);
          });
        }
      } catch (err) {
        console.error(err);
        alert("Could not start or resume session");
        if (!existingSessionId) {
          setAppStage("home");
        }
      } finally {
        setLoading(false);
      }
    },
    [playAudioUrl, updateJourneyFromResponse]
  );

  useEffect(() => {
    const chatContainer = chatContainerRef.current;

    if (!chatContainer) {
      return;
    }

    chatContainer.scrollTop = chatContainer.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (initialSessionStartedRef.current) {
      return;
    }

    initialSessionStartedRef.current = true;

    const savedSessionId = localStorage.getItem(SESSION_STORAGE_KEY);
    const savedJourneyId = localStorage.getItem(JOURNEY_STORAGE_KEY);

    if (!savedSessionId) {
      if (savedJourneyId) {
        setJourneyId(savedJourneyId);
      }

      setAppStage("home");
      return;
    }

    startOrResumeSession(savedSessionId, savedJourneyId);
  }, [startOrResumeSession]);

  useEffect(() => {
    preloadProcessingAcknowledgment();
  }, [preloadProcessingAcknowledgment]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      stopMediaStream();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      stopMediaStream();
    };
  }, [stopMediaStream]);

  useEffect(() => {
    const savedJobId = localStorage.getItem(
      BOOK_GENERATION_JOB_STORAGE_KEY
    );

    if (!savedJobId) {
      return;
    }

    const parsedJobId = Number(savedJobId);

    if (!Number.isFinite(parsedJobId)) {
      localStorage.removeItem(BOOK_GENERATION_JOB_STORAGE_KEY);
      return;
    }

    setBookGenerationJobId(parsedJobId);
    setBookGenerationStatus({
      job_id: parsedJobId,
      status: "queued",
      stage: "queued",
      progress_percent: 0,
      status_message: "Your book is queued for generation.",
    });
    setShowBookGenerationProgress(true);
  }, []);

  useEffect(() => {
    if (!bookGenerationJobId) {
      return;
    }

    let cancelled = false;
    let intervalId: number | null = null;

    async function pollLoop() {
      const result = await pollBookGenerationStatus(bookGenerationJobId!);

      if (cancelled) {
        return;
      }

      if (
        (result === "completed" || result === "failed") &&
        intervalId !== null
      ) {
        window.clearInterval(intervalId);
      }
    }

    void pollLoop();
    intervalId = window.setInterval(pollLoop, 3000);

    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [bookGenerationJobId, pollBookGenerationStatus]);

  useEffect(() => {
    if (!optionsMenuOpen) {
      return;
    }

    function closeOptionsMenuOnOutsidePress(event: MouseEvent | TouchEvent) {
      const optionsMenu = optionsMenuRef.current;

      if (
        !optionsMenu ||
        !(event.target instanceof Node) ||
        optionsMenu.contains(event.target)
      ) {
        return;
      }

      setOptionsMenuOpen(false);
    }

    document.addEventListener("mousedown", closeOptionsMenuOnOutsidePress);
    document.addEventListener("touchstart", closeOptionsMenuOnOutsidePress);

    return () => {
      document.removeEventListener(
        "mousedown",
        closeOptionsMenuOnOutsidePress
      );
      document.removeEventListener(
        "touchstart",
        closeOptionsMenuOnOutsidePress
      );
    };
  }, [optionsMenuOpen]);

  async function startNewSession() {
    setDoneForNowMessage(null);
    setDoneForNowError(null);
    localStorage.removeItem(SESSION_STORAGE_KEY);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setLatestSpeechAudioUrl(null);
    setSpeechPlaybackBlocked(false);
    setInput("");
    setMessages([]);
    setSessionId(null);
    setSessionStatus(null);
    setBookPlan(null);
    setShowPlanReview(false);
    setStoryReview(null);
    setShowStoryReview(false);
    setStoryReviewError(null);
    resetBookGenerationState();
    setOptionsMenuOpen(false);

    await startOrResumeSession(
      null,
      journeyId || localStorage.getItem(JOURNEY_STORAGE_KEY),
      { forceNewSession: true }
    );
  }

  async function startNewLifeStory() {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(JOURNEY_STORAGE_KEY);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setLatestSpeechAudioUrl(null);
    setSpeechPlaybackBlocked(false);
    setInput("");
    setMessages([]);
    setSessionId(null);
    setJourneyId(null);
    setCurrentPhase(null);
    setSessionStatus(null);
    setBookPlan(null);
    setShowPlanReview(false);
    setStoryReview(null);
    setShowStoryReview(false);
    setStoryReviewError(null);
    resetBookGenerationState();
    setOptionsMenuOpen(false);

    await startOrResumeSession(null, null);
  }

  async function handleDoneForNow() {
    if (!sessionId) {
      setAppStage("home");
      return;
    }

    if (recording) {
      setDoneForNowError(
        "Please pause and reflect on your current recording before ending the session."
      );
      return;
    }

    if (processingVoiceTurn) {
      setDoneForNowError(
        "Your last story is still being saved. Please wait a moment before ending the session."
      );
      return;
    }

    setEndingSession(true);
    setDoneForNowError(null);

    try {
      const response = await fetch(
        `${API_BASE}/sessions/${sessionId}/done-for-now/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await parseJsonResponse(
        response,
        "Failed to end session."
      );

      updateJourneyFromResponse(data);
      setDoneForNowMessage(
        data.message || "Your story has been saved. You can come back anytime."
      );

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      localStorage.removeItem(SESSION_STORAGE_KEY);
      setSessionId(null);
      setSessionStatus(null);
      setLatestSpeechAudioUrl(null);
      setSpeechPlaybackBlocked(false);
      setRecording(false);
      setProcessingVoiceTurn(false);
      setVoiceTurnStatus(null);
      setRecordingFlowState("idle");
      setShowStoryReview(false);
      setShowPlanReview(false);
      setShowBookGenerationProgress(false);
      setOptionsMenuOpen(false);
      setAppStage("home");
    } catch (err) {
      console.error("Failed to end session", err);
      setDoneForNowError(
        "Could not save and close this session. Please try again."
      );
    } finally {
      setEndingSession(false);
    }
  }

  async function confirmDeleteStory() {
    const confirmed = window.confirm(
      "Are you sure you want to delete your story?"
    );

    if (!confirmed) {
      return;
    }

    await startNewLifeStory();
  }

  async function startRecording() {
    if (!sessionId) return;

    if (recordingFlowState !== "idle") {
      audioDebug("startRecording blocked: not idle", recordingFlowState);
      return;
    }

    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setRecordingError("Audio recording is not supported in this browser.");
      setRecordingFlowState("error");
      return;
    }

    try {
      setRecordingError(null);
      setRecordingFlowState("starting");
      cleanupRecordingResources();
      audioDebug("Starting recording");

      if (audioRef.current) {
        audioRef.current.pause();
      }

      if (USE_CHUNKED_AUDIO) {
        await startChunkedRecording();
        return;
      }

      await startFullBlobRecording();
    } catch (err) {
      audioDebug("startRecording failed", err);
      console.error(err);
      setRecordingError("Could not start recording. Please try again.");
      setRecordingFlowState("error");
      setVoiceTurnStatus(null);
      cleanupRecordingResources();
    }
  }

  async function startChunkedRecording() {
    if (!sessionId) return;

    audioDebug("Starting narrative turn");

    const startResponse = await fetchWithTimeout(
      `${API_BASE}/narrative-turns/start/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
        }),
      },
      15000
    );

    const startData = await parseJsonResponse(
      startResponse,
      "Failed to start narrative turn"
    );

    updateJourneyFromResponse(startData);

    if (
      typeof startData.turn_id === "undefined" ||
      startData.turn_id === null
    ) {
      throw new Error("Narrative turn start response did not include turn_id");
    }

    const turnId = Number(startData.turn_id);

    if (!Number.isFinite(turnId)) {
      throw new Error(
        "Narrative turn start response included an invalid turn_id"
      );
    }

    audioDebug("Narrative turn started", startData);
    currentTurnIdRef.current = turnId;
    setCurrentTurnId(turnId);
    chunkIndexRef.current = 0;
    chunkUploadPromisesRef.current = [];
    recordedBlobsRef.current = [];
    recordingStartedAtRef.current = Date.now();
    recordingStoppedAtRef.current = null;
    stopRequestedRef.current = false;
    finishStartedRef.current = false;
    onStopHandledRef.current = false;
    recordedChunkCountRef.current = 0;
    clearStopFinalizeTimeout();
    setRecordingError(null);
    setUploadedChunkCount(0);

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    mediaStreamRef.current = stream;
    logMediaStreamState("after getUserMedia");
    const mimeType = getRecordingMimeType();
    const mediaRecorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined
    );

    mediaRecorder.onstart = () => {
      audioDebug("MediaRecorder started with timeslice", {
        turnId,
        mimeType,
        chunkIntervalMs: CHUNK_INTERVAL_MS,
      });
    };
    mediaRecorder.ondataavailable = handleRecorderDataAvailable;
    mediaRecorder.onstop = () => {
      void handleRecorderStop();
    };
    mediaRecorder.onerror = (event) => {
      audioDebug("MediaRecorder error", event);
      setRecordingError("Recording failed. Please try again.");
      setRecordingFlowState("error");
      setVoiceTurnStatus(null);
      setRecording(false);
      stopMediaStream();
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(CHUNK_INTERVAL_MS);
    audioDebug("recording start", { mimeType });
    setRecording(true);
    setRecordingFlowState("recording");
    setVoiceTurnStatus("recording");
  }

  async function startFullBlobRecording() {
    if (!sessionId) return;

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    mediaStreamRef.current = stream;
    logMediaStreamState("after getUserMedia");
    audioChunksRef.current = [];
    recordedBlobsRef.current = [];
    recordingStartedAtRef.current = null;
    recordingStoppedAtRef.current = null;
    stopRequestedRef.current = false;
    finishStartedRef.current = false;

    const mimeType = getRecordingMimeType();
    const mediaRecorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined
    );

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      stopMediaStream();
      setRecording(false);

      if (audioChunksRef.current.length === 0) {
        setRecordingFlowState("idle");
        setVoiceTurnStatus(null);
        return;
      }

      const audioBlob = new Blob(audioChunksRef.current, {
        type: mediaRecorder.mimeType || mimeType || "audio/webm",
      });

      await submitNarrativeTurnBlob(audioBlob);
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start();
    recordingStartedAtRef.current = Date.now();
    audioDebug("recording start", { mimeType });
    setRecording(true);
    setRecordingFlowState("recording");
    setVoiceTurnStatus("recording");
  }

  function stopRecording() {
    if (recordingFlowState !== "recording") {
      audioDebug("stopRecording ignored: not recording", recordingFlowState);
      return;
    }

    const mediaRecorder = mediaRecorderRef.current;

    if (!mediaRecorder) {
      stopMediaStream();
      setRecordingError("No active recording found.");
      setRecordingFlowState("error");
      return;
    }

    if (mediaRecorder.state !== "recording") {
      audioDebug("stopRecording ignored because recorder is not recording", {
        state: mediaRecorder.state,
      });
      return;
    }

    const elapsedMs = recordingStartedAtRef.current
      ? Date.now() - recordingStartedAtRef.current
      : 0;

    if (elapsedMs < MIN_RECORDING_MS) {
      audioDebug("recording stop blocked: too short", { elapsedMs });
      setRecordingError("Please speak for a few seconds before pausing.");
      return;
    }

    audioDebug("Pause & Reflect pressed", {
      elapsedMs,
      expectedChunksAtStop: expectedChunkCountFromDuration(elapsedMs),
    });
    stopRequestedRef.current = true;
    recordingStoppedAtRef.current = Date.now();
    setRecordingFlowState("stopping");
    setRecording(false);
    setVoiceTurnStatus("acknowledging");
    setRecordingError(null);

    playProcessingAcknowledgementAudio(processingAckAudioUrl);

    try {
      // Do not call requestData() immediately before stop().
      // In testing, this caused the final partial chunk to be unreliable.
      // Calling stop() should cause MediaRecorder to emit the final dataavailable event.
      mediaRecorder.stop();
      audioDebug("recorder.stop called");
    } catch (err) {
      audioDebug("stopRecording failed", err);
      setRecordingError("Could not stop recording cleanly. Please try again.");
      setRecordingFlowState("error");
      setVoiceTurnStatus(null);
      stopMediaStream();
      return;
    }

    stopFinalizeTimeoutRef.current = window.setTimeout(() => {
      if (!onStopHandledRef.current) {
        audioDebug("MediaRecorder onstop timeout");
        setRecordingError("Recording did not finish cleanly. Please try again.");
        setRecordingFlowState("error");
        setVoiceTurnStatus(null);
        setRecording(false);
        stopMediaStream();
        cleanupRecordingResources();
      }
    }, 10000);
  }

  function handleRecorderDataAvailable(event: BlobEvent) {
    const blob = event.data;

    audioDebug("MediaRecorder dataavailable", {
      size: blob?.size,
      type: blob?.type,
      stopRequested: stopRequestedRef.current,
      recorderState: mediaRecorderRef.current?.state,
      elapsedMs: recordingStartedAtRef.current
        ? Date.now() - recordingStartedAtRef.current
        : null,
    });

    if (!blob || blob.size === 0) {
      audioDebug("Ignoring empty audio blob");
      return;
    }

    recordedBlobsRef.current.push(blob);

    const turnId = currentTurnIdRef.current;

    if (!turnId) {
      audioDebug("No turn ID available for audio chunk");
      return;
    }

    const chunkIndex = chunkIndexRef.current;
    chunkIndexRef.current += 1;
    recordedChunkCountRef.current += 1;

    const uploadPromise = uploadAudioChunk(turnId, blob, chunkIndex);
    chunkUploadPromisesRef.current.push(uploadPromise);

    audioDebug("Queued audio chunk upload", {
      turnId,
      chunkIndex,
      size: blob.size,
      totalQueuedUploads: chunkUploadPromisesRef.current.length,
      totalRecordedBlobs: recordedBlobsRef.current.length,
    });
  }

  async function handleRecorderStop() {
    if (onStopHandledRef.current) {
      audioDebug("handleRecorderStop ignored; already handled");
      return;
    }

    onStopHandledRef.current = true;
    clearStopFinalizeTimeout();

    audioDebug("MediaRecorder onstop fired", {
      queuedUploads: chunkUploadPromisesRef.current.length,
      recordedChunkCount: recordedChunkCountRef.current,
      recordedBlobCount: recordedBlobsRef.current.length,
      stopRequested: stopRequestedRef.current,
      elapsedAtStopMs:
        recordingStartedAtRef.current && recordingStoppedAtRef.current
          ? recordingStoppedAtRef.current - recordingStartedAtRef.current
          : null,
    });

    stopMediaStream();

    const turnId = currentTurnIdRef.current;

    if (!turnId) {
      setRecordingError("Recording stopped, but no story turn was created.");
      setRecordingFlowState("error");
      setVoiceTurnStatus(null);
      return;
    }

    await delay(500);
    await finalizeStoppedRecording(turnId);
  }

  async function uploadAudioChunk(
    turnId: number,
    audioBlob: Blob,
    index: number
  ) {
    audioDebug("upload chunk begin", {
      turnId,
      index,
      size: audioBlob.size,
      type: audioBlob.type,
    });

    const formData = new FormData();
    formData.append("audio", audioBlob, `chunk-${index}.webm`);
    formData.append("index", String(index));

    const response = await fetchWithTimeout(
      `${API_BASE}/narrative-turns/${turnId}/chunks/`,
      {
        method: "POST",
        body: formData,
      },
      30000
    );

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      audioDebug("upload chunk failed", {
        turnId,
        index,
        status: response.status,
        data,
      });
      throw new Error(
        data?.message || `Failed to upload audio chunk ${index}.`
      );
    }

    updateJourneyFromResponse(data);
    audioDebug("upload chunk success", {
      turnId,
      index,
      data,
    });
    setUploadedChunkCount((prev) => prev + 1);
    return data;
  }

  async function finalizeStoppedRecording(turnId: number) {
    if (finishStartedRef.current) {
      audioDebug("finalizeStoppedRecording skipped: already started");
      return;
    }

    finishStartedRef.current = true;

    try {
      setProcessingVoiceTurn(true);
      setRecordingFlowState("uploading");
      setVoiceTurnStatus("transcribing");

      const clientRecordingDurationMs =
        recordingStartedAtRef.current && recordingStoppedAtRef.current
          ? recordingStoppedAtRef.current - recordingStartedAtRef.current
          : recordingStartedAtRef.current
            ? Date.now() - recordingStartedAtRef.current
            : null;
      const expectedChunkCount = expectedChunkCountFromDuration(
        clientRecordingDurationMs
      );
      const uploadPromises = [...chunkUploadPromisesRef.current];

      audioDebug("finish begin; checking chunk completeness", {
        turnId,
        clientRecordingDurationMs,
        expectedChunkCount,
        uploadPromiseCount: uploadPromises.length,
        recordedChunkCount: recordedChunkCountRef.current,
        recordedBlobCount: recordedBlobsRef.current.length,
      });

      if (uploadPromises.length === 0) {
        throw new Error("No audio was captured. Please try again.");
      }

      audioDebug("final chunk validation", {
        expectedChunkCount,
        uploadPromiseCount: uploadPromises.length,
        recordedBlobCount: recordedBlobsRef.current.length,
      });

      if (
        expectedChunkCount !== null &&
        uploadPromises.length < expectedChunkCount
      ) {
        throw new Error(
          `Recording did not finish uploading. Expected ${expectedChunkCount} audio chunks but only found ${uploadPromises.length}. Please try again.`
        );
      }

      const results = await Promise.allSettled(uploadPromises);
      const failedUploads = results.filter(
        (result) => result.status === "rejected"
      );

      if (failedUploads.length > 0) {
        audioDebug("Chunk upload failures before finish", failedUploads);
        throw new Error(
          "One or more audio chunks failed to upload. Please try again."
        );
      }

      setRecordingFlowState("reflecting");
      setVoiceTurnStatus("reflecting");

      const response = await fetchWithTimeout(
        `${API_BASE}/narrative-turns/${turnId}/finish/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            wait_timeout_seconds: 15,
            client_recording_duration_ms: clientRecordingDurationMs,
            client_chunk_count: uploadPromises.length,
          }),
        },
        60000
      );

      const data: any = await response.json().catch(() => null);

      if (!response.ok) {
        if (data?.error === "audio_transcription_failed") {
          throw new Error(
            data.message ||
              "We could not process that recording. Please try recording that part again."
          );
        }

        throw new Error(data?.message || "Failed to process your story.");
      }

      audioDebug("finish success", {
        turnId,
        data,
      });
      updateJourneyFromResponse(data);

      if (
        typeof data?.user_text !== "string" ||
        typeof data?.response !== "string"
      ) {
        throw new Error("Finished narrative turn response was incomplete");
      }

      if (data.timings) {
        setLastTurnTimings(data.timings);
        console.log("Chunked turn timings:", data.timings);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content: data.user_text,
        },
        {
          role: "assistant",
          content: data.response,
        },
      ]);

      audioDebug("Finished narrative turn", data);
      setRecordingFlowState("speaking");
      setVoiceTurnStatus("speaking");
      if (data.audio_url) {
        await waitForProcessingAcknowledgementToFinish();
        await playAudioUrl(data.audio_url);
      } else {
        await playOpenAITts(data.response, selectedVoice);
      }
      setRecordingFlowState("idle");
      setVoiceTurnStatus(null);
      setRecordingError(null);
    } catch (err) {
      audioDebug("finalizeStoppedRecording failed", err);
      console.error(err);
      const message =
        err instanceof Error
          ? err.message
          : "Something went wrong processing your story. Please try again.";
      setRecordingError(message);
      setRecordingFlowState("error");
      setVoiceTurnStatus(null);
    } finally {
      setProcessingVoiceTurn(false);
      cleanupRecordingResources();
    }
  }

  async function submitNarrativeTurnBlob(audioBlob: Blob) {
    if (!sessionId) return;

    try {
      setProcessingVoiceTurn(true);
      setVoiceTurnStatus("transcribing");

      const formData = new FormData();

      formData.append("session_id", String(sessionId));
      formData.append("audio", audioBlob, "story.webm");

      const response = await fetch(`${API_BASE}/narrative-turn/`, {
        method: "POST",
        body: formData,
      });

      const data = await parseJsonResponse(
        response,
        "Narrative turn failed"
      );

      updateJourneyFromResponse(data);

      if (
        typeof data.user_text !== "string" ||
        typeof data.response !== "string"
      ) {
        throw new Error("Narrative turn response was incomplete");
      }

      if (data.timings) {
        setLastTurnTimings(data.timings);
        console.log("Narrative turn timings:", data.timings);
      }

      setVoiceTurnStatus("reflecting");

      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content: data.user_text,
        },
        {
          role: "assistant",
          content: data.response,
        },
      ]);

      setVoiceTurnStatus("speaking");
      await playOpenAITts(data.response, selectedVoice);
      setRecordingFlowState("idle");
      setVoiceTurnStatus(null);
    } catch (err) {
      console.error(err);
      setRecordingError(
        "Something went wrong processing your story. Please try again."
      );
      setRecordingFlowState("error");
      setVoiceTurnStatus(null);
    } finally {
      setProcessingVoiceTurn(false);
      cleanupRecordingResources();
    }
  }

  async function playOpenAITts(text: string, voice = TTS_VOICE) {
    try {
      const response = await fetch(`${API_BASE}/speak/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text,
          voice,
        }),
      });

      const data = await parseJsonResponse(
        response,
        "Failed to create speech audio"
      );

      if (typeof data.audio_url !== "string") {
        throw new Error("Speech response did not include audio_url");
      }

      const audioUrl = resolveBackendUrl(data.audio_url);

      setLatestSpeechAudioUrl(audioUrl);
      setSpeechPlaybackBlocked(false);

      try {
        await waitForProcessingAcknowledgementToFinish();
        await playAudioUrl(audioUrl);
      } catch (err) {
        if (isPlaybackBlockedError(err)) {
          setSpeechPlaybackBlocked(true);
          return;
        }

        throw err;
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function replayLatestSpeech() {
    if (!latestSpeechAudioUrl) return;

    try {
      await playAudioUrl(latestSpeechAudioUrl);
      setSpeechPlaybackBlocked(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function sendMessage() {
    if (!input.trim()) return;

    if (!sessionId) return;

    const userMessage: Message = {
      role: "user",
      content: input,
    };

    setMessages((prev) => [...prev, userMessage]);

    const currentInput = input;

    setInput("");

    setProcessingVoiceTurn(true);

    try {
      const response = await fetch(`${API_BASE}/turn/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
          user_text: currentInput,
        }),
      });

      const data = await parseJsonResponse(
        response,
        "Failed to send message"
      );

      updateJourneyFromResponse(data);

      if (typeof data.response !== "string") {
        throw new Error("Turn response did not include response text");
      }

      const aiMessage: Message = {
        role: "assistant",
        content: data.response,
      };

      setMessages((prev) => [...prev, aiMessage]);
      await playOpenAITts(data.response, selectedVoice);
    } catch (err) {
      console.error(err);

      alert("Failed to send message");
    } finally {
      setProcessingVoiceTurn(false);
    }
  }

  async function handleReviewStoryProgress() {
    if (!journeyId) {
      setStoryReviewError("No story journey is available yet.");
      return;
    }

    setLoadingStoryReview(true);
    setStoryReviewError(null);

    try {
      const response = await fetch(
        `${API_BASE}/journeys/${journeyId}/story-review/`
      );

      const data = await parseJsonResponse(
        response,
        "Failed to load story progress."
      );

      updateJourneyFromResponse(data);
      setStoryReview(data);
      setShowStoryReview(true);
      setShowPlanReview(false);
      setShowBookGenerationProgress(false);
      setOptionsMenuOpen(false);
      setAppStage("story_review");
    } catch (err) {
      console.error("Failed to load story review", err);
      setStoryReviewError(
        "Could not load your story progress. Please try again."
      );
    } finally {
      setLoadingStoryReview(false);
    }
  }

  async function handleHomeReviewStoryProgress() {
    await handleReviewStoryProgress();
  }

  async function handleStartStoryGap(gap: StoryGapQuestion) {
    setStartingStoryGapId(gap.id);
    setStoryReviewError(null);

    try {
      const response = await fetch(`${API_BASE}/story-gaps/${gap.id}/start/`, {
        method: "POST",
      });

      const data = await parseJsonResponse(
        response,
        "Failed to start story question."
      );

      updateJourneyFromResponse(data);

      if (data.current_phase) {
        setCurrentPhase(data.current_phase);
      }

      if (Array.isArray(data.messages)) {
        setMessages(data.messages);
      } else if (data.response) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.response,
          },
        ]);
      }

      if (typeof data.audio_url === "string") {
        const audioUrl = resolveBackendUrl(data.audio_url);

        setLatestSpeechAudioUrl(audioUrl);
        setSpeechPlaybackBlocked(false);

        void playAudioUrl(audioUrl).catch((err) => {
          if (isPlaybackBlockedError(err)) {
            setSpeechPlaybackBlocked(true);
            return;
          }

          console.warn("Story gap audio playback failed", err);
        });
      }

      setShowStoryReview(false);
      setShowPlanReview(false);
      setShowBookGenerationProgress(false);
      setOptionsMenuOpen(false);
      setRecordingFlowState("idle");
      setVoiceTurnStatus(null);
      setAppStage("interview");
    } catch (err) {
      console.error("Failed to start story gap", err);
      setStoryReviewError(
        "Could not start that story question. Please try again."
      );
    } finally {
      setStartingStoryGapId(null);
    }
  }

  async function handleSkipStoryGap(gap: StoryGapQuestion) {
    setSkippingStoryGapId(gap.id);
    setStoryReviewError(null);

    try {
      const response = await fetch(`${API_BASE}/story-gaps/${gap.id}/skip/`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to skip story question.");
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await response.json().catch(() => null);
        if (data) {
          updateJourneyFromResponse(data);
        }
      }

      await handleReviewStoryProgress();
    } catch (err) {
      console.error("Failed to skip story gap", err);
      setStoryReviewError(
        "Could not skip that question. Please try again."
      );
    } finally {
      setSkippingStoryGapId(null);
    }
  }

  async function generateBookPlan() {
    if (!sessionId) return;

    try {
      setOptionsMenuOpen(false);
      setLoading(true);
      setShowStoryReview(false);
      setShowBookGenerationProgress(false);

      const response = await fetch(`${API_BASE}/book-plan/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
        }),
      });

      const data = await parseJsonResponse(
        response,
        "Failed to create book plan"
      );

      updateJourneyFromResponse(data);

      if (!data.plan) {
        throw new Error("No book plan returned");
      }

      resetBookGenerationState();
      setBookPlan(data.plan);
      setShowPlanReview(true);
      setAppStage("book_plan_review");
    } catch (err) {
      console.error(err);
      alert("Failed to create book plan");
    } finally {
      setLoading(false);
    }
  }

  function addMoreDetails() {
    setShowPlanReview(false);
    setShowBookGenerationProgress(false);
    setAppStage("interview");

    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content:
          "Great. Let's add more detail before creating the book. Which chapter or memory would you like to expand on?",
      },
    ]);
  }

  async function generateBookFromPlan() {
    if (!sessionId || !bookPlan) return;

    try {
      setLoading(true);
      setBookGenerationError(null);
      setBookDownloadUrl(null);

      const response = await fetch(`${API_BASE}/generate-from-plan/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
          plan: bookPlan,
        }),
      });

      const data = await parseJsonResponse(
        response,
        "Failed to start book generation"
      );

      updateJourneyFromResponse(data);

      if (typeof data.job_id === "undefined" || data.job_id === null) {
        throw new Error("No job ID returned");
      }

      const jobId = Number(data.job_id);

      if (!Number.isFinite(jobId)) {
        throw new Error("Invalid job ID returned");
      }

      const initialStatus = normalizeBookGenerationStatus(data, jobId);

      setBookGenerationJobId(jobId);
      setBookGenerationStatus(initialStatus);
      setBookDownloadUrl(initialStatus.download_url || null);
      setShowBookGenerationProgress(true);
      setShowPlanReview(false);
      setShowStoryReview(false);
      setAppStage("book_generation_progress");
      localStorage.setItem(
        BOOK_GENERATION_JOB_STORAGE_KEY,
        String(jobId)
      );
    } catch (err) {
      console.error(err);
      setBookGenerationError("Could not start book generation.");
      setShowBookGenerationProgress(true);
      setAppStage("book_generation_progress");
      localStorage.removeItem(BOOK_GENERATION_JOB_STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }

  function handleBackToStoryReviewFromBookProgress() {
    setShowBookGenerationProgress(false);
    setShowPlanReview(false);

    if (storyReview) {
      setShowStoryReview(true);
      setAppStage("story_review");
      return;
    }

    void handleReviewStoryProgress();
  }

  const statusText = getVoiceTurnStatusText();
  const canStartRecording =
    recordingFlowState === "idle" &&
    !processingVoiceTurn &&
    !!sessionId;
  const canStopRecording = recordingFlowState === "recording";

  if (appStage === "home") {
    return (
      <HomeScreen
        message={doneForNowMessage}
        canReviewStoryProgress={!!journeyId}
        loading={loading}
        loadingStoryReview={loadingStoryReview}
        onStartNewSession={startNewSession}
        onReviewStoryProgress={handleHomeReviewStoryProgress}
      />
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.brandText}>
            <h1 style={styles.title}>Life Story Simply</h1>
            <p style={styles.tagline}>Your story, remembered</p>
          </div>
          {sessionId && (
            <div ref={optionsMenuRef} style={styles.optionsWrapper}>
              <button
                type="button"
                aria-label="Options"
                aria-expanded={optionsMenuOpen}
                style={styles.optionsButton}
                onClick={() =>
                  setOptionsMenuOpen((open) => !open)
                }
              >
                <span style={styles.hamburgerLine} />
                <span style={styles.hamburgerLine} />
                <span style={styles.hamburgerLine} />
              </button>
              {optionsMenuOpen && (
                <div style={styles.optionsMenu}>
                  <button
                    type="button"
                    style={styles.optionsMenuButton}
                    onClick={startNewSession}
                    disabled={loading}
                  >
                    {loading ? "Starting..." : "Start New Session"}
                  </button>
                  <button
                    type="button"
                    style={styles.optionsMenuButton}
                    onClick={handleReviewStoryProgress}
                    disabled={loading || loadingStoryReview}
                  >
                    {loadingStoryReview
                      ? "Loading Story Progress..."
                      : "Review Story Progress"}
                  </button>
                  <button
                    type="button"
                    style={styles.deleteStoryButton}
                    onClick={confirmDeleteStory}
                    disabled={loading}
                  >
                    Delete Story
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {sessionStatus === "resumed" && (
          <div style={styles.sessionNotice}>
            Continuing this reflection session.
          </div>
        )}
        {currentPhase &&
          !showStoryReview &&
          !showPlanReview &&
          !showBookGenerationProgress && (
          <div style={styles.sessionNotice}>
            {PHASE_LABELS[currentPhase] || "Life story journey"}
          </div>
        )}
        {storyReviewError && (
          <div style={styles.errorMessage}>
            <span>{storyReviewError}</span>
          </div>
        )}
        {!showStoryReview &&
          !showPlanReview &&
          !showBookGenerationProgress &&
          (bookGenerationStatus ||
            bookDownloadUrl ||
            bookGenerationError) && (
            <div
              aria-label={
                bookGenerationJobId
                  ? `Book generation job ${bookGenerationJobId}`
                  : "Book generation job"
              }
              style={styles.bookGenerationNotice}
            >
              {bookGenerationStatus &&
                !bookDownloadUrl &&
                !bookGenerationError && (
                  <span>
                    {bookGenerationStatus.status === "queued" &&
                      "Your book is queued for generation..."}
                    {bookGenerationStatus.status === "running" &&
                      "Your book is being generated. This may take a few minutes..."}
                    {bookGenerationStatus.status !== "queued" &&
                      bookGenerationStatus.status !== "running" &&
                      "Checking your book generation status..."}
                  </span>
                )}
              {bookDownloadUrl && (
                <a
                  href={bookDownloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.bookDownloadLink}
                >
                  Download Book
                </a>
              )}
              {bookGenerationError && (
                <span style={styles.bookGenerationError}>
                  {bookGenerationError}
                </span>
              )}
            </div>
          )}
        {showBookGenerationProgress ? (
          <BookGenerationProgress
            status={bookGenerationStatus}
            error={bookGenerationError}
            onBackToStoryReview={handleBackToStoryReviewFromBookProgress}
          />
        ) : showStoryReview && storyReview ? (
          <StoryReviewView
            storyReview={storyReview}
            onBackToInterview={() => {
              setShowStoryReview(false);
              setAppStage("interview");
            }}
            onStartGap={handleStartStoryGap}
            onSkipGap={handleSkipStoryGap}
            onReviewBookPlan={generateBookPlan}
            startingStoryGapId={startingStoryGapId}
            skippingStoryGapId={skippingStoryGapId}
            loading={loading || loadingStoryReview}
          />
        ) : showPlanReview && bookPlan ? (
          <BookPlanReview
            plan={bookPlan}
            onGenerateBook={generateBookFromPlan}
            onAddMoreDetails={addMoreDetails}
            bookGenerationStatus={bookGenerationStatus}
            bookDownloadUrl={bookDownloadUrl}
            bookGenerationError={bookGenerationError}
          />
        ) : (
          <>
            {!sessionId && (
              <button
                style={styles.startButton}
                onClick={startNewSession}
                disabled={loading}
              >
                {loading ? "Starting..." : "Start New Session"}
              </button>
            )}
            {loading && (
              <div
                role="status"
                aria-label="Loading"
                style={styles.loadingIndicator}
              >
                <span
                  aria-hidden="true"
                  className="life-story-loading-wheel"
                  style={styles.loadingWheel}
                />
              </div>
            )}

            <div
              ref={chatContainerRef}
              role="log"
              aria-label="Conversation messages"
              style={styles.chatContainer}
            >
              {messages.map((message, index) => (
                <div
                  key={index}
                  style={{
                    ...styles.message,
                    ...(message.role === "user"
                      ? styles.userMessage
                      : styles.assistantMessage),
                  }}
                >
                  {message.content}
                </div>
              ))}
            </div>

            {speechPlaybackBlocked && latestSpeechAudioUrl && (
              <div style={styles.playbackNotice}>
                <span>
                  Audio is ready, but your browser needs a tap to play it.
                </span>
                <button
                  style={styles.playAudioButton}
                  onClick={replayLatestSpeech}
                >
                  Play Audio
                </button>
                <a
                  href={latestSpeechAudioUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.audioLink}
                >
                  Open audio
                </a>
              </div>
            )}

            {sessionId && (
              <div style={styles.inputRow}>
                <div style={styles.voiceTurnPanel}>
                  {statusText && (
                    <div style={styles.voiceStatus}>{statusText}</div>
                  )}
                  {recordingFlowState !== "error" && recordingError && (
                    <div style={styles.errorMessage}>
                      <span>{recordingError}</span>
                    </div>
                  )}
                  {recordingFlowState === "error" && recordingError && (
                    <div style={styles.errorMessage}>
                      <span>{recordingError}</span>
                      <button
                        style={styles.secondaryButton}
                        onClick={() => {
                          cleanupRecordingResources();
                          setRecordingError(null);
                          setRecordingFlowState("idle");
                          setVoiceTurnStatus(null);
                        }}
                      >
                        Reset Recording
                      </button>
                    </div>
                  )}
                  <button
                    style={styles.micButton}
                    onClick={recording ? stopRecording : startRecording}
                    disabled={
                      recording ? !canStopRecording : !canStartRecording
                    }
                  >
                    {recording
                      ? "Pause & Reflect"
                      : processingVoiceTurn
                        ? "Reflecting..."
                        : "Begin Speaking"}
                  </button>
                  {recording && (
                    <p style={styles.recordingGuidance}>
                      Take your time. You can pause while you think. Press
                      {" "}"Pause & Reflect"{" "}when you're done.
                    </p>
                  )}
                  {SHOW_DEBUG_TIMINGS && lastTurnTimings && (
                    <pre style={styles.debugTiming}>
                      {JSON.stringify(lastTurnTimings, null, 2)}
                    </pre>
                  )}
                </div>
                <label style={styles.typedResponseLabel}>
                  Or type your response
                  <textarea
                    style={styles.textarea}
                    placeholder="Type here..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    rows={3}
                  />
                </label>
                <button
                  style={styles.secondaryButton}
                  onClick={sendMessage}
                  disabled={loading || processingVoiceTurn}
                >
                  {processingVoiceTurn ? "Sending..." : "Send"}
                </button>
                {doneForNowError && (
                  <div style={styles.errorMessage}>
                    <span>{doneForNowError}</span>
                  </div>
                )}
                <button
                  type="button"
                  style={{
                    ...styles.doneForNowButton,
                    ...(endingSession || recording || processingVoiceTurn
                      ? styles.doneForNowButtonDisabled
                      : {}),
                  }}
                  onClick={handleDoneForNow}
                  disabled={endingSession || recording || processingVoiceTurn}
                >
                  {endingSession ? "Saving..." : "Done for now"}
                </button>
                {recording && (
                  <p style={styles.doneForNowHelper}>
                    Pause & Reflect first so we can save what you just said.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#f5f5f5",
    display: "flex",
    justifyContent: "center",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },

  container: {
    width: "100%",
    maxWidth: "700px",
    background: "white",
    borderRadius: "12px",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    height: "90vh",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  },

  homeContainer: {
    maxWidth: "720px",
    height: "auto",
    minHeight: "360px",
    justifyContent: "center",
    textAlign: "center",
  },

  homeContent: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },

  homeTitle: {
    margin: 0,
    fontSize: "32px",
    lineHeight: 1.15,
  },

  homeIntro: {
    margin: "0 0 6px",
    fontSize: "18px",
    lineHeight: 1.5,
    color: "#4b5563",
  },

  homeSavedMessage: {
    margin: "0 0 6px",
    padding: "16px",
    borderRadius: "8px",
    background: "#f3f4f6",
    color: "#374151",
    fontSize: "17px",
    lineHeight: 1.5,
  },

  homeActions: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "stretch",
  },

  homePrimaryButton: {
    marginBottom: 0,
  },

  header: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    background: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    margin: "-20px -20px 20px",
    padding: "14px 20px",
  },

  brandText: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  title: {
    margin: 0,
    fontSize: "24px",
    lineHeight: 1.15,
  },

  tagline: {
    margin: 0,
    color: "#6b7280",
    fontSize: "14px",
    lineHeight: 1.3,
  },

  sessionNotice: {
    alignSelf: "center",
    color: "#6b7280",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "999px",
    padding: "6px 12px",
    margin: "-8px 0 14px",
    fontSize: "13px",
  },

  bookGenerationNotice: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    color: "#1e3a8a",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "8px",
    padding: "10px 12px",
    margin: "-6px 0 14px",
    fontSize: "14px",
    lineHeight: 1.4,
  },

  bookDownloadLink: {
    color: "#166534",
    fontWeight: 700,
  },

  bookGenerationError: {
    color: "#991b1b",
  },

  bookProgressContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    overflowY: "auto",
    padding: "4px 2px 2px",
  },

  bookProgressHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },

  bookProgressTitle: {
    margin: 0,
    color: "#111827",
    fontSize: "24px",
    lineHeight: 1.2,
  },

  bookProgressStage: {
    flex: "0 0 auto",
    border: "1px solid #bfdbfe",
    borderRadius: "999px",
    background: "#eff6ff",
    color: "#1e3a8a",
    padding: "4px 10px",
    fontSize: "13px",
    lineHeight: 1.3,
    textTransform: "capitalize",
  },

  bookProgressMessage: {
    margin: 0,
    color: "#374151",
    fontSize: "16px",
    lineHeight: 1.5,
  },

  bookProgressOuter: {
    width: "100%",
    height: "14px",
    borderRadius: "999px",
    background: "#e5e7eb",
    overflow: "hidden",
  },

  bookProgressInner: {
    height: "100%",
    borderRadius: "999px",
    background: "#2563eb",
    transition: "width 250ms ease",
  },

  bookProgressMeta: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    color: "#4b5563",
    fontSize: "14px",
    lineHeight: 1.4,
  },

  primaryLinkButton: {
    display: "block",
    textAlign: "center",
    textDecoration: "none",
    padding: "14px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    background: "#166534",
    color: "white",
    fontSize: "16px",
    fontWeight: 700,
  },

  smallMutedText: {
    margin: 0,
    color: "#6b7280",
    fontSize: "14px",
    lineHeight: 1.5,
  },

  optionsWrapper: {
    position: "relative",
    flex: "0 0 auto",
  },

  optionsButton: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
  },

  hamburgerLine: {
    width: "18px",
    height: "2px",
    borderRadius: "999px",
    background: "#374151",
  },

  optionsMenu: {
    position: "absolute",
    top: "46px",
    right: 0,
    width: "180px",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
    padding: "8px",
    zIndex: 10,
  },

  optionsMenuButton: {
    width: "100%",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    background: "#f3f4f6",
    color: "#111827",
    padding: "10px",
    fontSize: "14px",
    textAlign: "left",
    marginBottom: "6px",
  },

  deleteStoryButton: {
    width: "100%",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    background: "#fee2e2",
    color: "#991b1b",
    padding: "10px",
    fontSize: "14px",
    textAlign: "left",
  },

  startButton: {
    padding: "16px",
    fontSize: "18px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    marginBottom: "20px",
    background: "#222",
    color: "white",
  },

  chatContainer: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "20px",
  },

  loadingIndicator: {
    width: "46px",
    height: "46px",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    margin: "0 0 18px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
  },

  loadingWheel: {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    border: "3px solid #d1d5db",
    borderTopColor: "#222",
    boxSizing: "border-box",
  },

  playbackNotice: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: "8px",
    padding: "10px 12px",
    marginBottom: "12px",
    fontSize: "14px",
  },

  playAudioButton: {
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    background: "#222",
    color: "white",
    padding: "8px 12px",
    fontSize: "14px",
  },

  audioLink: {
    color: "#9a3412",
  },

  message: {
    padding: "14px",
    borderRadius: "12px",
    maxWidth: "85%",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },

  userMessage: {
    alignSelf: "flex-end",
    background: "#dbeafe",
  },

  assistantMessage: {
    alignSelf: "flex-start",
    background: "#f1f1f1",
  },

  inputRow: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  voiceTurnPanel: {
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "14px",
    background: "#fafafa",
  },

  recordingGuidance: {
    margin: "10px 0 0",
    color: "#525252",
    fontSize: "15px",
    lineHeight: 1.5,
  },

  voiceStatus: {
    margin: "0 0 10px",
    color: "#111827",
    fontSize: "15px",
    fontWeight: 600,
    lineHeight: 1.5,
    textAlign: "center",
  },

  errorMessage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
    margin: "0 0 10px",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    fontSize: "14px",
    lineHeight: 1.4,
  },

  debugTiming: {
    margin: "12px 0 0",
    padding: "10px",
    borderRadius: "8px",
    background: "#111827",
    color: "#f9fafb",
    fontSize: "12px",
    lineHeight: 1.4,
    overflowX: "auto",
  },

  typedResponseLabel: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: "#6b7280",
    fontSize: "14px",
  },

  textarea: {
    width: "100%",
    resize: "none",
    borderRadius: "10px",
    padding: "12px",
    fontSize: "16px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  sendButton: {
    padding: "14px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    background: "#222",
    color: "white",
    fontSize: "16px",
  },

  secondaryButton: {
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    cursor: "pointer",
    background: "#f9fafb",
    color: "#374151",
    fontSize: "15px",
  },

  micButton: {
    width: "100%",
    padding: "16px",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    background: "#222",
    color: "white",
    fontSize: "18px",
    fontWeight: 700,
  },

  doneForNowButton: {
    alignSelf: "center",
    marginTop: "2px",
    background: "transparent",
    border: "none",
    color: "#374151",
    cursor: "pointer",
    fontSize: "15px",
    textDecoration: "underline",
  },

  doneForNowButtonDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
  },

  doneForNowHelper: {
    margin: "-2px 0 0",
    color: "#6b7280",
    fontSize: "14px",
    lineHeight: 1.4,
    textAlign: "center",
  },
};
